import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { quotationService } from '../../services/quotationService';
import type { QuotationListItemDto } from '../../types/quotation.types';
import { useQuotationWorkspaceStore } from '../../store/quotationWorkspaceStore';

interface Props {
  selectedId: number | null;
  onSelect: (id: number) => void;
}

// Single-row chip strip above the detail panel. Shows up to 5 pinned drafts
// for fast comparison work — click a chip to swap the active row, X to unpin.
// State (pinnedIds[]) lives in the workspace store; this component just
// renders + resolves names by id-batch.
//
// Hidden when there are no pins so the detail panel gets the full vertical
// space in the common case.

export function QuotationPinnedStrip({ selectedId, onSelect }: Props) {
  const pinnedIds = useQuotationWorkspaceStore((s) => s.pinnedIds);
  const togglePin = useQuotationWorkspaceStore((s) => s.togglePin);
  const [labels, setLabels] = useState<Map<number, QuotationListItemDto>>(new Map());

  // Pin labels lag the underlying data — we resolve names by reusing the list
  // endpoint (cheap, already paged). For unbounded scale we'd want a dedicated
  // GET /quotations?ids=… but at our 5-pin cap this is fine.
  useEffect(() => {
    if (pinnedIds.length === 0) {
      setLabels(new Map());
      return;
    }
    let cancelled = false;
    // Pull a generous slice and filter — handles the case where some pins are
    // older drafts that aren't in the active-only filter view.
    quotationService
      .listPaged({ page: 1, pageSize: 100 })
      .then((res) => {
        if (cancelled) return;
        const map = new Map<number, QuotationListItemDto>();
        for (const item of res.items) {
          if (pinnedIds.includes(item.id)) map.set(item.id, item);
        }
        setLabels(map);
      })
      .catch(() => {
        // Silent — chips fall back to "#id" labels if the resolve fails.
      });
    return () => {
      cancelled = true;
    };
  }, [pinnedIds]);

  if (pinnedIds.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1 border-b bg-muted/30 px-3 py-2">
      <span className="mr-1 text-[11px] uppercase tracking-wider text-muted-foreground">
        Pinned
      </span>
      {pinnedIds.map((id) => {
        const item = labels.get(id);
        const label = item
          ? (item.customerName || item.projectName || item.quotationNumber)
          : `#${id}`;
        const active = id === selectedId;
        return (
          <div
            key={id}
            className={cn(
              'group flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors',
              active
                ? 'border-primary bg-primary/10 text-foreground'
                : 'bg-background hover:bg-accent',
            )}
          >
            <button
              type="button"
              onClick={() => onSelect(id)}
              className="max-w-[180px] truncate text-left font-medium"
              title={label}
            >
              {label}
            </button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-4 w-4 shrink-0 p-0 opacity-50 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                togglePin(id);
              }}
              aria-label="Unpin"
              title="Unpin"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}
